import React from 'react';
import { Rocket, Users, ShieldCheck, Zap, Globe } from 'lucide-react';
import { StatItem, ThemeMode } from '../types';
import { useI18n } from '../i18n/I18nContext';

interface StatsSectionProps {
  stats: StatItem[];
  theme: ThemeMode;
}

export const StatsSection: React.FC<StatsSectionProps> = ({ stats, theme }) => {
  const isDark = theme === 'dark';
  const { t } = useI18n();

  const renderStatIcon = (iconName: string) => {
    const props = { className: "w-4 h-4 sm:w-6 sm:h-6" };
    switch (iconName) {
      case 'Rocket': return <Rocket {...props} />;
      case 'Users': return <Users {...props} />;
      case 'ShieldCheck': return <ShieldCheck {...props} />;
      case 'Zap': return <Zap {...props} />;
      case 'Globe': return <Globe {...props} />;
      default: return <Rocket {...props} />;
    }
  };

  const renderStatCard = (stat: StatItem) => (
    <div key={stat.id} className="flex items-center gap-1.5 sm:gap-4 min-w-0">
      <div className={`w-7 h-7 sm:w-12 sm:h-12 rounded-full flex items-center justify-center shrink-0 ${stat.colorClass}`}>
        {renderStatIcon(stat.iconName)}
      </div>
      <div className="min-w-0">
        <div className={`text-xs sm:text-2xl font-black tracking-tight leading-tight truncate ${
          isDark ? 'text-white' : 'text-slate-900'
        }`}>
          {stat.value}
        </div>
        <div className={`text-[8.5px] sm:text-sm font-medium leading-tight mt-0.5 sm:mt-0 truncate ${
          isDark ? 'text-slate-400' : 'text-slate-600'
        }`}>
          {t(stat.id, stat.label)}
        </div>
      </div>
    </div>
  );

  return (
    <section className="py-3 sm:py-8 px-3 sm:px-6 lg:px-8 max-w-7xl mx-auto z-10 relative">
      <div className={`p-3.5 sm:p-8 rounded-xl sm:rounded-2xl border transition-all duration-300 ${
        isDark
          ? 'bg-[#0f121e]/90 border-slate-800/90 shadow-xl'
          : 'bg-white border-slate-200/90 shadow-md shadow-slate-200/60'
      }`}>
        {/* Mobile Layout: 3 items on row 1, 2 centered items on row 2 */}
        <div className="block sm:hidden space-y-3">
          {/* Row 1: 3 items */}
          <div className="grid grid-cols-3 gap-x-1.5 items-center">
            {stats.slice(0, 3).map(renderStatCard)}
          </div>
          {/* Row 2: 2 items centered */}
          <div className="flex items-center justify-center gap-x-6 pt-1">
            {stats.slice(3).map(renderStatCard)}
          </div>
        </div>

        {/* Desktop / Tablet Layout: Single Grid Row */}
        <div className="hidden sm:grid sm:grid-cols-3 lg:grid-cols-5 gap-4 items-center justify-between">
          {stats.map(renderStatCard)}
        </div>
      </div>
    </section>
  );
};
